import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { API_CONFIG } from '../constants/env';
import { refreshWithRaceRetry } from '../api/apiClient';

/**
 * SignalR WebSocket service for real-time agent status updates
 * Implements Azure best practices for WebSocket connections
 */
class AgentStatusWebSocketService {
	constructor() {
		this.connection = null;
		this.subscribers = new Map();
		this.reconnectAttempts = 0;
		this.maxReconnectAttempts = 5;
		this.reconnectInterval = 5000; // 5 seconds
		this.isConnecting = false;
		this.storedBaseUrl = null;
		this.storedAccessToken = null;
		// 'idle' (never started / signed out) | 'connecting' | 'connected' | 'reconnecting' | 'disconnected'
		this.connectionStatus = 'idle';
		// Kept separate from `subscribers` because disconnect() clears those, and the app-level
		// banner listening here must survive sign-out / sign-in.
		this.statusListeners = new Set();
		this.isStopping = false;
		// Pending scheduleReconnect() timer; cleared by disconnect() so a sign-out can't be
		// followed by a reconnect with no credentials.
		this.reconnectTimer = null;
	}

	/**
	 * Update the coarse connection status and tell status listeners.
	 * Drives the offline/reconnecting banner so stale data is never shown silently.
	 */
	setConnectionStatus(status) {
		if (this.connectionStatus === status) return;
		this.connectionStatus = status;
		this.statusListeners.forEach((listener) => {
			try {
				listener(status);
			} catch (_error) {
				// A failing listener must not break the others
			}
		});
	}

	/**
	 * Listen for connection status changes. Survives disconnect(). Returns an unsubscribe function.
	 */
	onConnectionStatusChange(listener) {
		this.statusListeners.add(listener);
		return () => this.statusListeners.delete(listener);
	}

	getConnectionStatus() {
		return this.connectionStatus;
	}

	/**
	 * Initialize WebSocket connection
	 * @param {string} accessToken - JWT token for authentication
	 * @param {string} baseUrl - The base URL of the API (deprecated - now uses API_CONFIG)
	 */
	async initialize(baseUrl, accessToken) {
		if (this.connection && this.connection.state === HubConnectionState.Connected) {
			return;
		}

		if (this.isConnecting) {
			return;
		}

		// Store parameters for reconnection attempts
		this.storedBaseUrl = baseUrl;
		this.storedAccessToken = accessToken;

		this.isConnecting = true;
		if (this.reconnectAttempts === 0) {
			this.setConnectionStatus('connecting');
		}

		// A retry (not the initial page-load attempt) means the previous negotiate/connect
		// failed — likely because the short-lived access-token cookie expired since. Proactively
		// refresh it first so this attempt's negotiate call isn't just doomed to repeat the same
		// 401. Reuses apiClient's own refresh/rotation logic; if the refresh token itself is dead,
		// apiClient's response interceptor already redirects to /login on that failure.
		if (this.reconnectAttempts > 0) {
			try {
				await refreshWithRaceRetry();
			} catch (_refreshError) {
				// Proceed anyway — connection.start() below will fail and reschedule as usual.
			}
		}

		try {
			// Determine the correct URL based on environment configuration
			const hubUrl = this.getHubUrl();

			this.connection = new HubConnectionBuilder()
				.withUrl(hubUrl, {
					withCredentials: true, // Important for cookie authentication
					skipNegotiation: false // Allow normal negotiation process
				})
				.withAutomaticReconnect({
					nextRetryDelayInMilliseconds: (retryContext) => {
						// Exponential backoff with jitter
						const delay = Math.min(1000 * Math.pow(2, retryContext.previousRetryCount), 30000);
						return delay + (Math.random() * 1000); // Add jitter
					}
				})
				.configureLogging(import.meta.env.DEV ? LogLevel.Information : LogLevel.Warning)
				.build();

			// Set up event handlers
			this.setupEventHandlers();

			// Start the connection
			await this.connection.start();

			this.reconnectAttempts = 0;
			this.isConnecting = false;
			this.setConnectionStatus('connected');
		} catch (error) {
			console.error('SignalR connection failed:', error.message);

			this.isConnecting = false;
			this.setConnectionStatus('reconnecting');
			this.scheduleReconnect();
			throw error;
		}
	}

	/**
	 * Get the correct hub URL based on environment configuration
	 * Follows the established API_CONFIG pattern for environment management
	 */
	getHubUrl() {
		// Use the established environment configuration pattern
		const isDevelopment = import.meta.env.DEV;
		const config = isDevelopment ? API_CONFIG.development : API_CONFIG.production;

		// For SignalR hubs, use the configured baseURL
		const hubBaseUrl = config.baseURL;

		// Normalize base URL by removing trailing slash and /api suffix if present
		const normalizedBaseUrl = hubBaseUrl.replace(/\/(api)?$/, '');

		// SignalR handles the protocol negotiation automatically - don't convert to WSS
		// Just use the HTTP/HTTPS URL and let SignalR choose the best transport
		const hubUrl = `${normalizedBaseUrl}/agentStatusHub`;

		return hubUrl;
	}

	/**
	 * Set up SignalR event handlers
	 */
	setupEventHandlers() {
		// Handle agent status updates
		this.connection.on('AgentStatusesUpdated', (agents) => {
			this.notifySubscribers('agentStatusesUpdated', agents);
		});

		// Handle one dashboard's filtered agent list (server only sends dashboards we joined)
		this.connection.on('DashboardAgentStatusesUpdated', (dashboardId, agents) => {
			this.notifySubscribers('dashboardAgentStatusesUpdated', { dashboardId, agents });
		});

		// Handle individual agent updates
		this.connection.on('AgentUpdated', (agentData) => {
			this.notifySubscribers('agentUpdated', agentData);
		});

		// Handle global updates
		this.connection.on('GlobalAgentUpdate', () => {
			this.notifySubscribers('globalAgentUpdate');
		});

		// Handle new email processing log push (Phase 4c)
		this.connection.on('NewEmailProcessingLog', (log) => {
			this.notifySubscribers('newEmailProcessingLog', log);
		});

		// Handle worker health alerts (e.g. a stuck RabbitMQ consumer channel)
		this.connection.on('WorkerHealthAlertRaised', (alert) => {
			this.notifySubscribers('workerHealthAlertRaised', alert);
		});

		// Handle connection events
		this.connection.onreconnecting((_error) => {
			this.setConnectionStatus('reconnecting');
			this.notifySubscribers('reconnecting');
		});

		this.connection.onreconnected((connectionId) => {
			this.reconnectAttempts = 0;
			this.setConnectionStatus('connected');
			this.notifySubscribers('reconnected');
		});

		this.connection.onclose((error) => {
			// A close we did not ask for (error OR a clean server-side close, e.g. a deploy) must
			// reconnect; only our own disconnect() stops for good.
			if (!this.isStopping) {
				this.setConnectionStatus('reconnecting');
			}
			this.notifySubscribers('disconnected');
			if (!this.isStopping) {
				this.scheduleReconnect();
			}
		});
	}

	/**
	 * Subscribe to client-specific agent updates
	 * @param {string} clientId - The client ID to subscribe to
	 */
	async subscribeToClientUpdates(clientId) {
		if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
			return;
		}

		await this.connection.invoke('SubscribeToClientUpdates', clientId);
	}

	/**
	 * Unsubscribe from client-specific agent updates
	 * @param {string} clientId - The client ID to unsubscribe from
	 */
	async unsubscribeFromClientUpdates(clientId) {
		if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
			return;
		}

		try {
			await this.connection.invoke('UnsubscribeFromClientUpdates', clientId);
		} catch (error) {
			// Silently handle errors during unsubscribe
		}
	}

	/**
	 * Subscribe to one dashboard's updates. The server checks access before joining the group
	 * and rejects (HubException) when the user may not see the dashboard.
	 * @param {string} clientId - The client the dashboard belongs to
	 * @param {string} dashboardId - Dashboard GUID ('all' is routed to the client subscription)
	 */
	async subscribeToDashboardUpdates(clientId, dashboardId) {
		if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
			return;
		}

		await this.connection.invoke('SubscribeToDashboardUpdates', clientId, dashboardId);
	}

	async unsubscribeFromDashboardUpdates(dashboardId) {
		if (!this.connection || this.connection.state !== HubConnectionState.Connected) {
			return;
		}

		try {
			await this.connection.invoke('UnsubscribeFromDashboardUpdates', dashboardId);
		} catch (error) {
			// Silently handle errors during unsubscribe
		}
	}

	/**
	 * Subscribe to WebSocket events
	 * @param {string} event - Event name
	 * @param {Function} callback - Callback function
	 * @returns {Function} Unsubscribe function
	 */
	subscribe(event, callback) {
		if (!this.subscribers.has(event)) {
			this.subscribers.set(event, new Set());
		}

		this.subscribers.get(event).add(callback);

		// Return unsubscribe function
		return () => {
			const eventSubscribers = this.subscribers.get(event);
			if (eventSubscribers) {
				eventSubscribers.delete(callback);
				if (eventSubscribers.size === 0) {
					this.subscribers.delete(event);
				}
			}
		};
	}

	/**
	 * Notify all subscribers of an event
	 */
	notifySubscribers(event, data) {
		const eventSubscribers = this.subscribers.get(event);
		if (eventSubscribers) {
			eventSubscribers.forEach(callback => {
				try {
					callback(data);
				} catch (error) {
					// Silently handle callback errors to prevent breaking other subscribers
				}
			});
		}
	}

	/**
	 * Schedule a reconnect attempt. Never permanently gives up — retries indefinitely with
	 * backoff capped at 30s, since a dashboard with a silently-dead live connection is worse
	 * than one that keeps trying. maxReconnectAttempts only shapes the backoff curve now, it
	 * is not a give-up threshold.
	 */
	scheduleReconnect() {
		this.reconnectAttempts++;
		const delay = Math.min(this.reconnectInterval * this.reconnectAttempts, 30000);

		clearTimeout(this.reconnectTimer);
		this.reconnectTimer = setTimeout(() => {
			this.reconnectTimer = null;
			if (!this.connection || this.connection.state === HubConnectionState.Disconnected) {
				this.initialize(this.storedBaseUrl, this.storedAccessToken).catch(() => {
					// Silently handle initialization errors during reconnect
				});
			}
		}, delay);
	}

	/**
	 * Get connection state
	 */
	getConnectionState() {
		return this.connection ? this.connection.state : HubConnectionState.Disconnected;
	}

	/**
	 * Check if connected
	 */
	isConnected() {
		return this.connection && this.connection.state === HubConnectionState.Connected;
	}

	/**
	 * Disconnect the WebSocket connection
	 */
	async disconnect() {
		this.isStopping = true;
		clearTimeout(this.reconnectTimer);
		this.reconnectTimer = null;
		if (this.connection) {
			try {
				await this.connection.stop();
			} catch (error) {
				// Silently handle disconnection errors
			}
		}

		this.setConnectionStatus('idle');
		this.isStopping = false;

		// Clear subscribers
		this.subscribers.clear();
		this.connection = null;
		this.isConnecting = false;
		this.reconnectAttempts = 0;
		this.storedBaseUrl = null;
		this.storedAccessToken = null;
	}
}

// Create and export singleton instance
const agentStatusWebSocketService = new AgentStatusWebSocketService();
export default agentStatusWebSocketService;
